import { useCallback, useRef, useState } from "react";
import { Dialog } from "../../../../../components/Dialog/Dialog.tsx";
import { DropdownItem } from "../../../../../components/Dropdown/DropdownItem.tsx";
import { ClickableIcon } from "../../../../../components/Icon/ClickableIcon.tsx";
import { Popover } from "../../../../../components/Popover/Popover.tsx";
import { Spinner } from "../../../../../components/Spinner/Spinner.tsx";
import { useSelectedModelsSettingsAndActions } from "../../../../boardState/hooks/useSelectedModelsSettingsAndActions.ts";
import { boardModelPickerStore } from "../../../../boardState/stores/boardModelPickerStore.ts";
import { STYLE_MODEL_TYPE, UPLOAD_MAX_IMAGES } from "../../../../constants.ts";
import type { ModelType, StyleModelImagesKind } from "../../../../types.ts";
import { MODEL_TYPE_LABELS_WITH_ARTICLE } from "../../../display.ts";
import { useBoardUuid } from "../../../hooks/useBoardUuid.ts";
import { useModelUpload } from "../../../hooks/useModelUpload.ts";
import type { ModelCompatibleTool } from "../../../types.ts";
import { StyleImageKindSelectorContent } from "./StyleImageKindSelectorContent.tsx";

export const AddModelButton = ({
  tool,
  modelType,
  isDisabled,
  allowDirectUpload = false,
}: {
  tool: ModelCompatibleTool;
  modelType: ModelType;
  isDisabled: boolean;
  allowDirectUpload?: boolean;
}) => {
  if (!allowDirectUpload) {
    return (
      <ClickableIcon
        name="Plus"
        size="sm"
        variant="tertiary"
        onClick={() =>
          boardModelPickerStore.setState({
            tool,
            modelType,
            isOpen: true,
          })
        }
        disabled={isDisabled}
        tooltip={{
          side: "right",
          content: isDisabled
            ? `Not possible to add ${MODEL_TYPE_LABELS_WITH_ARTICLE[modelType]}, please reset your model selection`
            : `Add ${MODEL_TYPE_LABELS_WITH_ARTICLE[modelType]}`,
        }}
      />
    );
  }
  return (
    <AddOrUploadModelDropdown
      tool={tool}
      modelType={modelType}
      isDisabled={isDisabled}
    />
  );
};

const AddOrUploadModelDropdown = ({
  tool,
  modelType,
  isDisabled,
}: {
  tool: ModelCompatibleTool;
  modelType: ModelType;
  isDisabled: boolean;
}) => {
  const boardUuid = useBoardUuid();
  const [isOpen, setIsOpen] = useState(false);
  const [showStyleKindDialog, setShowStyleKindDialog] = useState(false);
  const [isInStyleUploadFlow, setIsInStyleUploadFlow] = useState(false);
  const { modelActions } = useSelectedModelsSettingsAndActions(tool, boardUuid);

  const selectedStyleKindRef = useRef<StyleModelImagesKind | null>(null);

  const { openFileBrowser, getInputProps, isLoading } = useModelUpload(
    modelType,
    UPLOAD_MAX_IMAGES,
    (modelUuid, isModelTrained) => {
      modelActions.addModel(modelUuid, isModelTrained);
      setIsOpen(false);
      setIsInStyleUploadFlow(false);
    },
    modelType === STYLE_MODEL_TYPE
      ? () => selectedStyleKindRef.current ?? undefined
      : undefined,
  );

  const handleUploadClick = useCallback(() => {
    if (modelType === STYLE_MODEL_TYPE) {
      setIsInStyleUploadFlow(true);
      setShowStyleKindDialog(true);
    } else {
      openFileBrowser();
    }
  }, [modelType, openFileBrowser]);

  const handleStyleKindSelect = useCallback(
    (kind: StyleModelImagesKind) => {
      setShowStyleKindDialog(false);
      selectedStyleKindRef.current = kind;
      openFileBrowser();
    },
    [openFileBrowser],
  );

  const handlePopoverOpenChange = useCallback(
    (open: boolean) => {
      // XXX: Prevent closing popover if we're in the middle of style pipeline
      if (!open && isInStyleUploadFlow) return;
      setIsOpen(open);

      // XXX: Reset states when popover closes
      if (!open) {
        setShowStyleKindDialog(false);
        setIsInStyleUploadFlow(false);
        selectedStyleKindRef.current = null;
      }
    },
    [isInStyleUploadFlow],
  );

  return (
    <>
      <Popover
        side="bottom"
        align="end"
        sideOffset={4}
        isOpen={isOpen}
        onOpenChange={handlePopoverOpenChange}
        content={
          <div className="flex-col p-200 w-[208px]">
            <div className="relative">
              <div className={`flex-col ${isLoading ? "invisible" : ""}`}>
                <DropdownItem
                  onClick={() => {
                    boardModelPickerStore.setState({
                      tool,
                      modelType,
                      isOpen: true,
                    });
                    setIsOpen(false);
                  }}
                >
                  Select from library
                </DropdownItem>

                <DropdownItem onClick={handleUploadClick} disabled={isLoading}>
                  <input {...getInputProps()} />
                  Upload up to {UPLOAD_MAX_IMAGES} images
                </DropdownItem>
              </div>
              {isLoading && (
                <div className="absolute inset-0 flex-col-center gap-200 justify-center">
                  <Spinner size="sm" />
                  <div className="body-sm-default">Processing...</div>
                </div>
              )}
            </div>
          </div>
        }
      >
        <ClickableIcon
          name="Plus"
          size="sm"
          variant="tertiary"
          className="p-0"
          disabled={isLoading || isDisabled}
          onClick={() => setIsOpen(true)}
          tooltip={{
            side: "right",
            content: isDisabled
              ? `Not possible to add ${MODEL_TYPE_LABELS_WITH_ARTICLE[modelType]}, please reset your model selection`
              : `Add ${MODEL_TYPE_LABELS_WITH_ARTICLE[modelType]}`,
          }}
        />
      </Popover>

      {modelType === STYLE_MODEL_TYPE && (
        <Dialog
          isOpen={showStyleKindDialog}
          onOpenChange={setShowStyleKindDialog}
          title="What best describes your style?"
          content={
            <StyleImageKindSelectorContent
              onKindSelect={handleStyleKindSelect}
            />
          }
        />
      )}
    </>
  );
};
